import React, { useState, useEffect, useMemo } from "react";
import Table, { BID_COLUMNS } from "./Table";
import { connect } from "react-redux";
import { GET } from "../constant/apiMethods";
import { GET_BIDDING_AUCTION } from "../constant";
import Labels from "../services/apiLabels";
import { getAuthUserId } from "../services/localStorage";
import { FiSearch, FiX } from "react-icons/fi";
import { PaginationControl } from "react-bootstrap-pagination-control";
import DetailDrawer, { pickValue } from "./DetailDrawer";

const TAB_CONFIG = [
  {
    name: "Active",
    listKey: "active_auction",
    countKey: "active_auction_count",
    columns: BID_COLUMNS.active,
    dateKeys: ["bidOffer", "bid_on", "bid_date"],
    amountKeys: ["myBidOffer", "currentBid", "offer"],
    searchKeys: ["registration", "vehicle", "timeLeft", "status"],
    statusKey: null,
  },
  {
    name: "Upcoming",
    listKey: "upcoming_auction",
    countKey: "upcoming_auction_count",
    columns: BID_COLUMNS.upcoming,
    dateKeys: ["startIn", "start_in", "start_date"],
    amountKeys: ["amount", "myBidOffer", "opening_price", "price"],
    searchKeys: ["registration", "vehicle", "status"],
    statusKey: "status",
  },
  {
    name: "Lost",
    listKey: "lost_auction",
    countKey: "lost_auction_count",
    columns: BID_COLUMNS.lost,
    dateKeys: ["bidOffer", "bid_on", "bid_date"],
    amountKeys: ["myBidOffer", "winningBid", "offer"],
    searchKeys: ["registration", "vehicle"],
    statusKey: null,
  },
  {
    name: "Won",
    listKey: "won_auction",
    countKey: "won_auction_count",
    columns: BID_COLUMNS.won,
    dateKeys: ["boughtOn", "bought_on", "purchase_date"],
    amountKeys: ["price", "myBidOffer", "amount"],
    searchKeys: ["registration", "vehicle", "status"],
    statusKey: "status",
  },
];

const BID_DETAIL_FIELDS = [
  {
    label: "Registration",
    keys: ["registration", "reg", "registration_number"],
  },
  { label: "Vehicle", keys: ["vehicle", "product_name", "name"] },
  {
    label: "Bid on",
    keys: ["bidOffer", "bid_on", "bid_date", "created_at"],
    type: "date",
  },
  {
    label: "Bought on",
    keys: ["boughtOn", "bought_on", "purchase_date"],
    type: "date",
  },
  {
    label: "Start in",
    keys: ["startIn", "start_in", "startDate", "start_date"],
    type: "date",
  },
  {
    label: "Time left",
    keys: ["timeLeft", "time_left", "remaining", "endDate", "end_date"],
  },
  {
    label: "Your offer",
    keys: ["myBidOffer", "offer", "bid_amount", "my_bid"],
    type: "money",
  },
  {
    label: "Current bid",
    keys: ["currentBid", "current_bid", "cur_price", "current_price"],
    type: "money",
  },
  {
    label: "Winning bid",
    keys: ["winningBid", "winning_bid", "final_bid"],
    type: "money",
  },
  {
    label: "Price",
    keys: ["price", "amount", "opening_price", "openingPrice"],
    type: "money",
  },
  { label: "Status", keys: ["status", "auction_status"] },
  { label: "Product ID", keys: ["product_id", "productId", "id", "p_id"] },
];

const PAGE_SIZE_OPTIONS = [5, 10, 20];

function toTime(value) {
  if (value == null || value === "") return 0;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? 0 : t;
}

function toNumber(value) {
  const n = Number(value);
  return Number.isNaN(n) ? 0 : n;
}

function itemMatchesSearch(item, query, searchKeys) {
  if (!query) return true;
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return searchKeys.some((key) => {
    const value = item?.[key];
    return value != null && String(value).toLowerCase().includes(q);
  });
}

const Tabs = ({ getBiddingAuction, biddingAuction }) => {
  const [selectedTabIdx, setSelectedTabIdx] = useState(0);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("date_desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [viewingBid, setViewingBid] = useState(null);

  const id = getAuthUserId();
  const tab = TAB_CONFIG[selectedTabIdx];
  const sourceList = biddingAuction?.[tab.listKey] || [];

  useEffect(() => {
    if (id) getBiddingAuction(id);
  }, []);

  useEffect(() => {
    setPage(1);
    setStatusFilter("all");
    setSearch("");
    setSortBy("date_desc");
    setViewingBid(null);
  }, [selectedTabIdx]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, sortBy, pageSize]);

  const statusOptions = useMemo(() => {
    if (!tab.statusKey) return [];
    const set = new Set();
    sourceList.forEach((item) => {
      const status = item?.[tab.statusKey];
      if (status != null && String(status).trim()) set.add(String(status));
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [sourceList, tab.statusKey]);

  const filteredList = useMemo(() => {
    let rows = Array.isArray(sourceList) ? [...sourceList] : [];

    rows = rows.filter((item) =>
      itemMatchesSearch(item, search, tab.searchKeys)
    );

    if (tab.statusKey && statusFilter !== "all") {
      rows = rows.filter(
        (item) => String(item?.[tab.statusKey] || "") === statusFilter
      );
    }

    rows.sort((a, b) => {
      if (sortBy === "date_asc") {
        return (
          toTime(pickValue(a, tab.dateKeys)) - toTime(pickValue(b, tab.dateKeys))
        );
      }
      if (sortBy === "date_desc") {
        return (
          toTime(pickValue(b, tab.dateKeys)) - toTime(pickValue(a, tab.dateKeys))
        );
      }
      if (sortBy === "amount_asc") {
        return (
          toNumber(pickValue(a, tab.amountKeys)) -
          toNumber(pickValue(b, tab.amountKeys))
        );
      }
      if (sortBy === "amount_desc") {
        return (
          toNumber(pickValue(b, tab.amountKeys)) -
          toNumber(pickValue(a, tab.amountKeys))
        );
      }
      if (sortBy === "vehicle_asc") {
        return String(a?.vehicle || "").localeCompare(String(b?.vehicle || ""));
      }
      return 0;
    });

    return rows;
  }, [sourceList, search, statusFilter, sortBy, tab]);

  const totalPages = Math.max(1, Math.ceil(filteredList.length / pageSize) || 1);
  const safePage = Math.min(page, totalPages);
  const pageStart = filteredList.length ? (safePage - 1) * pageSize : 0;
  const pageEnd = Math.min(pageStart + pageSize, filteredList.length);
  const pagedList = filteredList.slice(pageStart, pageEnd);

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setSortBy("date_desc");
    setPage(1);
  };

  const hasActiveFilters =
    Boolean(search.trim()) || statusFilter !== "all" || sortBy !== "date_desc";

  return (
    <div className="dd-section">
      <nav className="dd-tabs" aria-label="Bid status">
        {TAB_CONFIG.map((item, index) => (
          <button
            key={item.name}
            type="button"
            className={`dd-tab${index === selectedTabIdx ? " is-active" : ""}`}
            onClick={() => setSelectedTabIdx(index)}
          >
            {item.name}
            <span className="dd-tab__count">
              {biddingAuction?.[item.countKey] || 0}
            </span>
          </button>
        ))}
      </nav>

      <div className="dd-toolbar">
        <label className="dd-toolbar__search">
          <FiSearch size={16} aria-hidden />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search reg, vehicle…"
            aria-label="Search bids"
          />
          {search ? (
            <button
              type="button"
              className="dd-toolbar__clear-input"
              onClick={() => setSearch("")}
              aria-label="Clear search"
            >
              <FiX size={14} />
            </button>
          ) : null}
        </label>

        {tab.statusKey ? (
          <label className="dd-toolbar__field">
            <span>Status</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All statuses</option>
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        <label className="dd-toolbar__field">
          <span>Sort</span>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="date_desc">Date · newest</option>
            <option value="date_asc">Date · oldest</option>
            <option value="amount_desc">Amount · high to low</option>
            <option value="amount_asc">Amount · low to high</option>
            <option value="vehicle_asc">Vehicle · A–Z</option>
          </select>
        </label>

        <label className="dd-toolbar__field">
          <span>Per page</span>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>

        {hasActiveFilters ? (
          <button
            type="button"
            className="dd-btn dd-btn--ghost"
            onClick={clearFilters}
          >
            Reset
          </button>
        ) : null}
      </div>

      <div className="dd-toolbar__meta">
        {filteredList.length ? (
          <span>
            Showing {pageStart + 1}–{pageEnd} of {filteredList.length}
            {filteredList.length !== sourceList.length
              ? ` (filtered from ${sourceList.length})`
              : ""}
          </span>
        ) : (
          <span>No matching records</span>
        )}
      </div>

      <Table
        biddingAuction={pagedList}
        bidColumns={tab.columns}
        onViewBid={setViewingBid}
      />

      {filteredList.length > pageSize ? (
        <div className="dd-pagination">
          <PaginationControl
            page={safePage}
            between={3}
            total={filteredList.length}
            limit={pageSize}
            changePage={(next) => setPage(next)}
            ellipsis={1}
          />
        </div>
      ) : null}

      <DetailDrawer
        item={viewingBid}
        eyebrow={`${tab.name} bid`}
        titleKeys={["vehicle", "product_name", "name"]}
        fields={BID_DETAIL_FIELDS}
        ariaLabel="Bid details"
        onClose={() => setViewingBid(null)}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  biddingAuction: state.profile.auction,
});

const mapDispatchToProps = (dispatch) => ({
  getBiddingAuction: (id) =>
    dispatch({
      type: GET_BIDDING_AUCTION,
      payload: {
        type: GET_BIDDING_AUCTION,
        label: `${Labels.get_bidding_auction}${id}`,
        method: GET,
        hideOverlayLoader: true,
      },
    }),
});

export default connect(mapStateToProps, mapDispatchToProps)(Tabs);
